import { GoogleAuthProvider, signInWithPopup, signOut, createUserWithEmailAndPassword, signInWithEmailAndPassword, getAuth, initializeAuth, isSignInWithEmailLink, signInWithEmailLink, sendSignInLinkToEmail } from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { initializeApp } from 'firebase/app';
import { initializeFirebase, getFirebaseInstance } from '../firebase/config';
import { trackLogin, trackSignUp, trackError, trackEvent } from '../firebase/analytics';

const FUNCTIONS_URL = 'https://us-central1-transferisok.cloudfunctions.net';

class AuthService {
  constructor() {
    const { auth, db } = getFirebaseInstance();
    this.auth = auth;
    this.db = db;
    this.googleProvider = new GoogleAuthProvider();
  }

  async signInWithGoogle() {
    try {
      const result = await signInWithPopup(this.auth, this.googleProvider);
      const user = result.user;

      // Registrar evento de login
      trackLogin('google');

      return user;
    } catch (error) {
      console.error('Error al iniciar sesión con Google:', error);
      trackError('Error al iniciar sesión con Google', error.code);
      throw error;
    }
  }

  async signInWithEmail(email, password) {
    try {
      const result = await signInWithEmailAndPassword(this.auth, email, password);

      // Registrar evento de login
      trackLogin('email_password');

      return result.user;
    } catch (error) {
      console.error('Error al iniciar sesión con email:', error);
      trackError('Error al iniciar sesión con email', error.code);
      throw error;
    }
  }

  async getUserData(email) {
    if (!email) return null;

    try {
      // Por ID: las reglas de Firestore dan permiso por el ID del documento, no por
      // el campo email, así que una consulta por el campo ya no pasa.
      const snap = await getDoc(doc(this.db, 'usuarios', email));
      return snap.exists() ? snap.data() : null;
    } catch (error) {
      throw error;
    }
  }

  async updateUserData(email, updateData) {
    if (!email) return null;

    try {
      const docRef = doc(this.db, 'usuarios', email);
      await updateDoc(docRef, updateData);
      return true;
    } catch (error) {
      console.error('Error al actualizar datos del usuario:', error);
      throw error;
    }
  }

  // Dice si un email ya tiene usuario. Se pregunta al backend porque las reglas de
  // Firestore no dejan leer documentos de otros, y esto corre antes del login.
  async usuarioExiste(email) {
    const response = await fetch(`${FUNCTIONS_URL}/verificarUsuario`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email })
    });

    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.message || 'No se pudo verificar el usuario');
    }
    return data.existe === true;
  }

  async signOutUser() {
    try {
      await signOut(this.auth);
      return true;
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
      trackError('Error al cerrar sesión', error.code);
      throw error;
    }
  }

  async getGoogleUserInfo() {
    const provider = new GoogleAuthProvider();
    const result = await signInWithPopup(this.auth, provider);
    return result.user;
  }

  async completeGoogleSignIn(user) {
    const token = await user.getIdToken();
    return { user, token };
  }

  async registerWithEmail(email, password, userData) {
    try {
      // Primero intentamos crear el usuario en Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(this.auth, email, password);

      // Si se creó exitosamente, creamos el documento en Firestore
      await this.createUserDocument(email, userData);

      return userCredential.user;
    } catch (error) {
      console.error('Error en registerWithEmail:', error);

      // Si el error es que el email ya existe, lanzamos ese error específico
      if (error.code === 'auth/email-already-in-use') {
        throw { code: 'auth/email-already-in-use' };
      }

      // Para cualquier otro error, lo propagamos
      throw error;
    }
  }

  async createUserDocument(email, userData) {
    try {
      const docRef = doc(this.db, 'usuarios', email);
      await setDoc(docRef, userData);
    } catch (error) {
      console.error('Error al crear documento:', error);
      throw error;
    }
  }

  handleAuthError(error) {
    switch (error.code) {
      case 'auth/email-already-in-use':
        return new Error('Este email ya está registrado');
      case 'auth/invalid-email':
        return new Error('Email inválido');
      case 'auth/operation-not-allowed':
        return new Error('Operación no permitida');
      case 'auth/weak-password':
        return new Error('La contraseña es muy débil');
      default:
        return new Error('Error en el registro');
    }
  }

  async registerWithGoogle() {
    try {
      const result = await signInWithPopup(this.auth, this.googleProvider);
      return {
        user: result.user,
        photoURL: result.user.photoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent(result.user.displayName || 'U')}&background=1a73e8&color=fff&size=40`,
        displayName: result.user.displayName
      };
    } catch (error) {
      throw error;
    }
  }

  isSignInWithEmailLink(link) {
    return isSignInWithEmailLink(this.auth, link);
  }

  async signInWithEmailLinkMethod(email, link) {
    try {
      if (isSignInWithEmailLink(this.auth, link)) {
        const result = await signInWithEmailLink(this.auth, email, link);
        return result;
      }
      throw new Error('Link inválido');
    } catch (error) {
      throw error;
    }
  }

  async signUpWithEmail(email, password) {
    try {
      const result = await createUserWithEmailAndPassword(this.auth, email, password);

      // Registrar evento de registro
      trackSignUp('email_password');

      return result.user;
    } catch (error) {
      console.error('Error al registrarse con email:', error);
      trackError('Error al registrarse con email', error.code);
      throw error;
    }
  }

  async sendLoginLink(email) {
    try {
      // En la Mac: sendLoginLink no acepta pedidos desde localhost (CORS) y su link
      // siempre vuelve a app.transferisok.com. Se le pide el link directo a Firebase,
      // que lo manda con su mail genérico y vuelve a este mismo origen.
      if (process.env.NODE_ENV === 'development' && window.location.hostname === 'localhost') {
        await sendSignInLinkToEmail(this.auth, email, {
          url: `${window.location.origin}/home?email=${encodeURIComponent(email)}`,
          handleCodeInApp: true
        });
        window.localStorage.setItem('emailForSignIn', email);
        return true;
      }

      const cloudFunctionUrl = 'https://us-central1-transferisok.cloudfunctions.net/sendLoginLink';

      const response = await fetch(cloudFunctionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ email })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Error desconocido' }));
        console.error('Error response:', errorData);
        trackError('Error al enviar el link de acceso', errorData.message);
        throw new Error(errorData.message || 'Error al enviar el link de acceso');
      }

      const data = await response.json();
      console.log('Respuesta del servidor:', data);

      // Guardamos el email para la verificación posterior
      window.localStorage.setItem('emailForSignIn', email);

      // Registrar evento de envío de link
      trackEvent('login_link_sent', { method: 'email_link' });

      return true;
    } catch (error) {
      console.error('Error detallado al enviar link de acceso:', error);
      trackError('Error al enviar link de acceso', error.message);
      throw error;
    }
  }
}

export const createAuthService = async () => {
  try {
    await initializeFirebase();
    return new AuthService();
  } catch (error) {
    console.error('Error al crear AuthService:', error);
    throw error;
  }
};

// Da de alta un operador. Lo hace el backend: el navegador ya no puede crear
// documentos de usuarios, porque el collector_id de un usuario decide de qué cuenta
// de Mercado Pago ve las transferencias. El backend toma la cuenta del admin que
// llama, nunca del pedido.
export const createOperator = async (userData) => {
  const { auth } = getFirebaseInstance();

  const adminUser = auth.currentUser;
  if (!adminUser?.email) {
    throw new Error('No hay usuario autenticado');
  }

  const idToken = await adminUser.getIdToken();
  const response = await fetch(`${FUNCTIONS_URL}/crearOperador`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${idToken}`
    },
    body: JSON.stringify({
      email: userData.email,
      fullName: userData.fullName,
      tienda: userData.tienda,
      rol: userData.admin ? 'administrador' : 'operador',
      limite: userData.limite ?? null,
      // Solo para el superadmin: alta en nombre del admin de otra cuenta.
      ...(userData.verComo && { verComo: userData.verComo })
    })
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(data.message || 'Error al crear operador. Por favor, intenta nuevamente.');
    // Mismo código que usaba el alta desde el navegador, para que el modal
    // muestre el mensaje de email repetido.
    if (data.error === 'ya_registrado') error.code = 'auth/email-already-in-use';
    throw error;
  }

  return true;
};
